import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

type Profile = Database['public']['Tables']['profiles']['Row'];
type Estado = 'carregando' | 'autenticado' | 'anonimo';

interface AuthContextType {
  user: any | null;
  profile: Profile | null;
  authState: Estado;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<Profile | null>;
  setProfile: (profile: Profile | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfileState] = useState<Profile | null>(null);
  const [authState, setAuthState] = useState<Estado>('carregando');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const buscarPerfil = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error("Erro ao buscar perfil:", error);
        return null;
      }
      
      setProfileState(data);
      return data;
    } catch (err) {
      console.error("Erro inesperado ao buscar perfil:", err);
      return null;
    }
  };

  useEffect(() => {
    let ativo = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!ativo) return;
      if (data.session) {
        setUser(data.session.user);
        setAuthState('autenticado');
        buscarPerfil(data.session.user.id);
      } else {
        setUser(null);
        setProfileState(null);
        setAuthState('anonimo');
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_ev, s) => {
      setUser(s?.user ?? null);
      setAuthState(s ? 'autenticado' : 'anonimo');
      if (s) {
        setTimeout(() => buscarPerfil(s.user.id), 0);
      } else {
        setProfileState(null);
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    });

    return () => {
      ativo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfileState(null);
    setAuthState('anonimo');
    navigate({ to: '/entrar' });
  };

  const refreshProfile = async () => {
    if (!user) return null;
    await supabase.auth.refreshSession();
    return buscarPerfil(user.id);
  };

  const setProfile = (data: Profile | null) => {
    setProfileState(data);
    queryClient.setQueryData(['profile', user?.id], data);
  };

  return (
    <AuthContext.Provider value={{ user, profile, authState, signOut, refreshProfile, setProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

